import React from 'react';
import CodeBlock from '../components/common/CodeBlock';
import BlogChessBoard from '../components/common/BlogChessBoard';

// Utility function to render formatted text.
// Supports: bold, italic, underline, headings, links, code blocks, chess boards,
// lichess iframes, and inline images.
// Inline images use short keys (e.g. ![alt](img:1)) resolved against the inlineImages map.
// Only img: prefixed references are rendered — arbitrary URLs are ignored for security.
export const renderFormattedText = (text, isMobile = false, inlineImages = {}) => {
  if (!text) return null;

  const result = [];
  let currentIndex = 0;

  // First, extract and process lichess iframes (before code blocks to avoid conflicts)
  const iframeRegex = /<iframe[^>]*src="https:\/\/lichess\.org\/study\/embed\/[^"]*"[^>]*><\/iframe>/gi;
  let processedText = text;
  const iframeMatches = [];
  let iframeMatch;

  while ((iframeMatch = iframeRegex.exec(text)) !== null) {
    iframeMatches.push({
      fullMatch: iframeMatch[0],
      index: iframeMatch.index,
      placeholder: `<<<LICHESS_IFRAME_${iframeMatch.index}>>>`
    });
  }

  // Replace iframes with placeholders to avoid them being processed as code
  iframeMatches.forEach(({ fullMatch, placeholder }) => {
    processedText = processedText.replace(fullMatch, placeholder);
  });

  // Now extract and process code blocks from the placeholder-replaced text
  const codeBlockRegex = /```([\w]*)\n?([\s\S]*?)```/g;
  let match;
  let lastIndex = 0;

  while ((match = codeBlockRegex.exec(processedText)) !== null) {
    // Process text before the code block
    if (match.index > lastIndex) {
      const textBefore = processedText.substring(lastIndex, match.index);
      result.push(...processNormalText(textBefore, currentIndex, iframeMatches, isMobile, inlineImages));
      currentIndex += textBefore.length;
    }

    // Check if this is a chess block
    const language = match[1] || '';
    const code = match[2].trim();

    if (language === 'chess') {
      // Parse FEN, PGN, Title, and Show Solution from chess block
      const fenMatch = code.match(/FEN:\s*(.+)$/m);
      const pgnMatch = code.match(/PGN:\s*(.+)$/m);
      const titleMatch = code.match(/Title:\s*(.+)$/m);
      const showSolutionMatch = code.match(/Show Solution:\s*(.+)$/m);

      const fen = fenMatch ? fenMatch[1].trim() : undefined;
      const pgn = pgnMatch ? pgnMatch[1].trim() : '';
      const title = titleMatch ? titleMatch[1].trim() : '';
      const showSolution = showSolutionMatch ? showSolutionMatch[1].trim().toLowerCase() !== 'false' : true;

      result.push(
        <BlogChessBoard
          key={`chess-${match.index}`}
          initialFen={fen}
          initialPgn={pgn}
          initialTitle={title}
          showMoves={showSolution}
          mode="live"
        />
      );
    } else {
      // Regular code block
      result.push(
        <CodeBlock
          key={`code-${match.index}`}
          code={code}
          language={language}
        />
      );
    }

    currentIndex += match[0].length;
    lastIndex = match.index + match[0].length;
  }

  // Process remaining text after the last code block
  if (lastIndex < processedText.length) {
    const remainingText = processedText.substring(lastIndex);
    result.push(...processNormalText(remainingText, currentIndex, iframeMatches, isMobile, inlineImages));
  }

  return result;
};

// Helper function to process inline formatting (bold, italic, underline, links)
const processInlineFormatting = (text) => {
  let formattedLine = text;
  const elementMap = new Map(); // Store elements by token for correct ordering

  // Handle links FIRST — before bold/italic, so **[text](url)** works correctly
  formattedLine = formattedLine.replace(/\[(.*?)\]\((.*?)\)/g, (match, linkText, url, offset) => {
    const trimmedUrl = url.trim();
    const trimmedText = linkText.trim();

    // Check if the URL looks valid
    const startsWithProtocol = /^https?:\/\//i.test(trimmedUrl);
    const startsWithWww = /^www\./i.test(trimmedUrl);
    const hasTld = /\.(com|org|net|edu|gov|io|co|ai|dev|app|site|info|biz|me|us|uk|ca|de|fr|jp|cn|in|au|br|ru|es|it|nl|se|no|dk|fi|be|ch|at|cz|pl|ie|nz|sg|hk|kr|mx|ar|cl|za|ae|sa|eg|il|tr|th|id|my|ph|vn|pk|bd|ng|ke|tz|gh|ug|zw|zm|mw|bw|na|sn|ci|cm|ao|et|sd|dz|tn|ma|ly|so|dj|er|bi|rw|lr|sl|gm|gn|bf|ne|td|cf|cg|ga|gq|st|cv|sc|mu|re|yt|km|mz|mg|bj|tg|ml|mr)($|\/)/i.test(trimmedUrl);

    // Determine the href
    let href;
    if (startsWithProtocol) {
      href = trimmedUrl;
    } else if (startsWithWww || hasTld) {
      href = `https://${trimmedUrl}`;
    } else {
      // Not a URL, use as-is
      href = trimmedUrl;
    }

    const token = `<<<LINK_${offset}>>>`;
    elementMap.set(token,
      <a
        key={`link-${offset}`}
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        style={{ color: '#3b82f6', textDecoration: 'underline', cursor: 'pointer' }}
      >
        {trimmedText || linkText}
      </a>
    );
    return token;
  });

  // Handle bold: **text** (after links, so **[text](url)** preserves the link token)
  formattedLine = formattedLine.replace(/\*\*(.*?)\*\*/g, (match, content, offset) => {
    const token = `<<<BOLD_${offset}>>>`;
    // Check if content contains a link token — if so, resolve it to wrap the link in bold
    const linkTokenMatch = content.match(/<<<LINK_(\d+)>>>/);
    if (linkTokenMatch) {
      const linkElement = elementMap.get(linkTokenMatch[0]);
      if (linkElement) {
        elementMap.delete(linkTokenMatch[0]);
        elementMap.set(token, <strong key={`bold-${offset}`}>{linkElement}</strong>);
        return token;
      }
    }
    elementMap.set(token, <strong key={`bold-${offset}`}>{content}</strong>);
    return token;
  });

  // Handle italic: *text*
  formattedLine = formattedLine.replace(/\*(.*?)\*/g, (match, content, offset) => {
    const token = `<<<ITALIC_${offset}>>>`;
    elementMap.set(token, <em key={`italic-${offset}`}>{content}</em>);
    return token;
  });

  // Handle underline: __text__
  formattedLine = formattedLine.replace(/__(.*?)__/g, (match, content, offset) => {
    const token = `<<<UNDERLINE_${offset}>>>`;
    elementMap.set(token, <u key={`underline-${offset}`}>{content}</u>);
    return token;
  });

  // Extract tokens in the order they appear in the text
  const tokens = formattedLine.match(/<<<[A-Z]+_\d+>>>/g) || [];
  const textParts = formattedLine.split(/<<<[A-Z]+_\d+>>>/);
  const result = [];

  // Interleave text parts with elements in the correct order
  for (let i = 0; i < textParts.length; i++) {
    if (textParts[i]) {
      result.push(<span key={`text-${i}`}>{textParts[i]}</span>);
    }
    if (i < tokens.length) {
      result.push(elementMap.get(tokens[i]));
    }
  }

  return result;
};

// Helper function to process normal text (non-code)
const processNormalText = (text, startKey, iframeMatches = [], isMobile = false, inlineImages = {}) => {
  if (!text) return [];

  // Check for iframe placeholders and replace them with actual iframes
  let processedText = text;
  const iframeElements = [];

  iframeMatches.forEach(({ fullMatch, placeholder }, idx) => {
    if (processedText.includes(placeholder)) {
      // Extract src from iframe
      const srcMatch = fullMatch.match(/src="([^"]*)"/);
      const widthMatch = fullMatch.match(/width="?(\d+)"?/);
      const heightMatch = fullMatch.match(/height="?(\d+)"?/);

      const src = srcMatch ? srcMatch[1] : '';
      const width = widthMatch ? widthMatch[1] : '600';
      const height = heightMatch ? heightMatch[1] : '371';

      // Only render if it's a valid lichess study URL
      if (src.includes('lichess.org/study/embed/')) {
        const iframeKey = `iframe-${startKey}-${idx}`;

        // Extract the study URL for the "View on Lichess" link (remove /embed from path)
        const studyUrl = src.replace('/study/embed/', '/study/');

        const iframeElement = isMobile ? (
          // Mobile: scaled down iframe with overflow hidden + link below
          <div key={iframeKey} className="my-4">
            <div style={{
              width: '100%',
              height: `${parseInt(height) * 0.51}px`,
              overflow: 'hidden',
              border: '1px solid #e5e7eb',
              borderRadius: '8px'
            }}>
              <div style={{ transform: 'scale(0.51)', transformOrigin: 'top left', width: '200%' }}>
                <iframe
                  src={src}
                  width={width}
                  height={height}
                  frameBorder="0"
                  title="Lichess Study"
                  style={{ maxWidth: 'none', display: 'block' }}
                />
              </div>
            </div>
            <a
              href={studyUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="block text-center mt-2 text-sm text-blue-400 hover:text-blue-300 transition-colors"
            >
              View full study on Lichess.org →
            </a>
          </div>
        ) : (
          // Desktop: Full width container
          <div key={iframeKey} className="my-4" style={{ width: '100%' }}>
            <iframe
              src={src}
              width="100%"
              height={height}
              frameBorder="0"
              title="Lichess Study"
              style={{ border: '1px solid #e5e7eb', borderRadius: '8px', display: 'block' }}
            />
          </div>
        );

        iframeElements.push({ placeholder, element: iframeElement });
        processedText = processedText.replace(placeholder, `<<<IFRAME_ELEMENT_${idx}>>>`);
      }
    }
  });

  // Extract inline images ![alt](img:N) and replace with placeholders
  const imageElements = [];
  processedText = processedText.replace(/!\[([^\]]*)\]\((img:\d+)\)/g, (match, alt, ref) => {
    const key = ref.replace('img:', '');
    const url = inlineImages[key];
    const placeholder = `<<<INLINE_IMG_${imageElements.length}>>>`;
    imageElements.push({
      placeholder,
      element: url ? (
        <div key={`img-${startKey}-${imageElements.length}`} style={{ width: '100%', display: 'flex', justifyContent: 'center', margin: '16px 0' }}>
          <img
            src={url}
            alt={alt.trim() || 'Blog image'}
            loading="lazy"
            style={{
              maxWidth: '100%',
              maxHeight: '600px',
              borderRadius: '8px',
              objectFit: 'contain'
            }}
          />
        </div>
      ) : (
        <div key={`img-${startKey}-${imageElements.length}`} style={{ width: '100%', display: 'flex', justifyContent: 'center', margin: '16px 0', padding: '24px', backgroundColor: '#374151', borderRadius: '8px', color: '#9ca3af', fontSize: '14px' }}>
          <i className="fas fa-image" style={{ marginRight: '8px' }}></i> Image not found ({ref})
        </div>
      )
    });
    return placeholder;
  });

  // Split by line breaks first
  const lines = processedText.split('\n');

  return lines.map((line, lineIndex) => {
    // Check if this line contains an inline image placeholder
    const imgMatch = line.match(/<<<INLINE_IMG_(\d+)>>>/);
    if (imgMatch) {
      const idx = parseInt(imgMatch[1]);
      if (imageElements[idx]) {
        return imageElements[idx].element;
      }
    }

    // Check if this line contains an iframe element placeholder
    const iframeElementMatch = line.match(/<<<IFRAME_ELEMENT_(\d+)>>>/);
    if (iframeElementMatch) {
      const idx = parseInt(iframeElementMatch[1]);
      const iframeData = iframeElements.find(ie => ie.placeholder === iframeMatches[idx]?.placeholder);
      if (iframeData) {
        return iframeData.element;
      }
    }

    if (line.trim() === '') {
      return <br key={`br-${startKey}-${lineIndex}`} />;
    }

    // Check if line is a heading (#, ##, ###)
    const headingMatch = line.match(/^(#{1,3})\s+(.+)$/);
    if (headingMatch) {
      const level = headingMatch[1].length; // 1-3 hashes
      const content = headingMatch[2];
      const HeadingTag = `h${level}`; // # → h1, ## → h2, ### → h3

      return (
        <HeadingTag
          key={`heading-${startKey}-${lineIndex}`}
          className={`text-${level === 1 ? '3xl' : level === 2 ? '2xl' : 'xl'} font-bold text-gray-100 ${level === 1 ? 'mt-4 mb-2' : level === 2 ? 'mt-3 mb-2' : 'mt-2 mb-1'}`}
        >
          {processInlineFormatting(content)}
        </HeadingTag>
      );
    }

    // Process inline formatting for normal lines
    const result = processInlineFormatting(line);

    return (
      <span key={`line-${startKey}-${lineIndex}`}>
        {result}
        {lineIndex < lines.length - 1 && <br />}
      </span>
    );
  });
};

// Alternative simpler version for basic formatting
export const renderSimpleFormattedText = (text) => {
  if (!text) return null;

  // Replace line breaks with <br> tags
  let formattedText = text.replace(/\n/g, '<br />');
  
  // Replace bold markers
  formattedText = formattedText.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
  
  // Replace italic markers
  formattedText = formattedText.replace(/\*(.*?)\*/g, '<em>$1</em>');
  
  // Replace underline markers
  formattedText = formattedText.replace(/__(.*?)__/g, '<u>$1</u>');

  // Replace markdown-style links with blue clickable links
  formattedText = formattedText.replace(/\[(.*?)\]\((.*?)\)/g, (match, linkText, url) => {
    const trimmedUrl = url.trim();
    const trimmedText = linkText.trim();

    // Check if the URL looks valid
    const startsWithProtocol = /^https?:\/\//i.test(trimmedUrl);
    const startsWithWww = /^www\./i.test(trimmedUrl);
    const hasTld = /\.(com|org|net|edu|gov|io|co|ai|dev|app|site|info|biz|me|us|uk|ca|de|fr|jp|cn|in|au|br|ru|es|it|nl|se|no|dk|fi|be|ch|at|cz|pl|ie|nz|sg|hk|kr|mx|ar|cl|za|ae|sa|eg|il|tr|th|id|my|ph|vn|pk|bd|ng|ke|tz|gh|ug|zw|zm|mw|bw|na|sn|ci|cm|ao|et|sd|dz|tn|ma|ly|so|dj|er|bi|rw|lr|sl|gm|gn|bf|ne|td|cf|cg|ga|gq|st|cv|sc|mu|re|yt|km|mz|mg|bj|tg|ml|mr)($|\/)/i.test(trimmedUrl);

    // Determine the href
    let href;
    if (startsWithProtocol) {
      href = trimmedUrl;
    } else if (startsWithWww || hasTld) {
      href = `https://${trimmedUrl}`;
    } else {
      // Not a URL, use as-is
      href = trimmedUrl;
    }

    return `<a href="${href}" target="_blank" rel="noopener noreferrer" style="color: #3b82f6; text-decoration: underline; cursor: pointer;">${trimmedText || linkText}</a>`;
  });

  return <span dangerouslySetInnerHTML={{ __html: formattedText }} />;
}; 